import styled from 'styled-components';

import {
  grey1,
  grey5,
  grey6,
  grey9,
  coolGrey1,
  purple7,
  grey10,
  azure7,
  purple5,
} from 'approot/shared/styles/colours.style';
import { rem } from 'approot/shared/style';
import {
  borderRadius8,
  standardFocusState,
} from 'approot/shared/styles/borders.style';
import {
  StandardInputProps,
  standardInputStyles,
} from 'approot/shared/styles/inputs.style';
import { fontWeightMedium } from 'approot/shared/styles/typography.style';
import {
  spacing12,
  spacing16,
  spacing8,
} from 'approot/shared/styles/spacing.style';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import Richtext from 'approot/shared/richtext/richtext';
import {
  RichtextProps,
  richtextCommonCss,
} from 'approot/shared/richtext/richtext.style';
import { useActivityBuilderFontStyles } from 'approot/activity-builder/theme/activity-builder-theme.style';
import { getLineMinHeight } from 'approot/activity-builder/theme/activity-builder-theme.utils';
import { TooltipWrapperStyled } from 'approot/shared/tooltip/tooltip.style';
import { flexCentreAlignCss } from 'approot/shared/styles/flex.style';

const RESIZE_HANDLE_Z_INDEX = 1;
const ACTIVE_ROWS_Z_INDEX = 2;
const INLINE_SETTINGS_Z_INDEX = 3;

export const TableWrapper = styled.div`
  position: relative;
`;

export const TableReadOnlyTrigger = styled.button`
  ${standardFocusState}

  background: transparent;
  border: 0;
  cursor: pointer;
  inset: 0;
  padding: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: ${INLINE_SETTINGS_Z_INDEX};

  &:hover {
    outline: 1px solid ${grey6};
    outline-offset: 2px;
  }
`;

export const Table = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  position: relative;
  width: 100%;
  height: 100%;

  ${TooltipWrapperStyled} {
    width: 100%;
    height: 100%;
  }
`;

type TableResizeHandleProps = {
  $isVisible: boolean;
};
export const TableResizeHandle = styled.button<TableResizeHandleProps>`
  ${standardFocusState}

  background-color: ${grey5};
  border-radius: ${rem(72)};
  border: none;
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  cursor: ew-resize;
  left: 0;
  margin: 0;
  margin-left: -${rem(4)};
  margin-top: -${rem(40)};
  padding: 0;
  position: absolute;
  top: 50%;
  width: ${rem(8)};
  height: ${rem(80)};
  z-index: ${RESIZE_HANDLE_Z_INDEX};

  &:hover,
  &:active {
    background-color: ${purple7};
  }
`;

type TableRowProps = {
  $isRowActive?: boolean;
};
export const TableRow = styled.tr<TableRowProps>`
  ${({ $isRowActive }) =>
    $isRowActive &&
    `
    td, th {
      &:first-child:after {
        content: '';
        border-left: 1px solid ${grey10};
      }
      &:last-child:after {
        content: "";
        border-right: 1px solid ${grey10};
      }
      &:after {
        content: '';
        border-top: 1px solid ${grey10};
        border-bottom: 1px solid ${grey10};
        display: inline-block;
        pointer-events: none;
        position: absolute;
        left: -1px;
        top: -1px;
        right: -1px;
        bottom: -1px;
        z-index: ${ACTIVE_ROWS_Z_INDEX};
      }
    }
  `}
`;

type TableCellProps = {
  $highlight?: boolean;
  $isColumnActive?: boolean;
  $isTopCell?: boolean;
  $isLastCell?: boolean;
};

export const TableCell = styled.td.attrs<TableCellProps>(({ $highlight }) => ({
  as: $highlight ? 'th' : 'td',
}))<TableCellProps>`
  background-color: ${({ $highlight }) => ($highlight ? coolGrey1 : grey1)};
  box-sizing: border-box;
  border: 1px solid ${grey5};
  margin: 0;
  padding: ${rem(2)};
  position: relative;

  .ProseMirror {
    border: 1px solid ${({ $highlight }) => ($highlight ? coolGrey1 : grey1)};
    border-radius: 0;
    ${({ $highlight }) =>
      $highlight &&
      `
        p {
          font-weight: ${fontWeightMedium};
        }
    `}
  }

  ${({ $isColumnActive, $isLastCell, $isTopCell }) =>
    $isColumnActive &&
    `
      &:after {
        content: '';
        border-left: 1px solid ${grey10};
        border-right: 1px solid ${grey10};
        display: inline-block;
        pointer-events: none;
        position: absolute;
        left: -1px;
        right: -1px;
        bottom: -1px;
        top: -1px;
        z-index: ${ACTIVE_ROWS_Z_INDEX};

        ${
          $isTopCell
            ? `
            border-top: 1px solid ${grey10};
          `
            : ''
        }

        ${
          $isLastCell
            ? `
            border-bottom: 1px solid ${grey10};
          `
            : ''
        }
      }
    `}
`;

type CommonProps = {
  $highlight?: boolean;
  $baseFontSize: number;
};

export const TableTextArea = styled(AutoResizingTextArea)<CommonProps>`
  ${() =>
    useActivityBuilderFontStyles({
      isSmall: true,
    })}

  border-radius: 0;
  border-color: ${grey1};
  display: block;
  min-height: ${({ $baseFontSize }) => getLineMinHeight($baseFontSize)};

  ${props =>
    props.$highlight &&
    `
      background-color: ${coolGrey1};
      border: 1px solid ${coolGrey1};
    `}
`;

export const TableCellView = styled.div.attrs<CommonProps & RichtextProps>({
  $isSmall: true,
})<
  CommonProps & {
    $rows?: number;
    $baseLineHeight?: number;
    $isAnswer?: boolean;
  }
>`
  color: ${({ $isAnswer }) => ($isAnswer ? azure7 : grey9)};
  border: 1px solid ${({ $highlight }) => ($highlight ? coolGrey1 : grey1)};
  height: 100%;
  min-height: ${({ $baseFontSize }) => rem(getLineMinHeight($baseFontSize))};
  padding: ${rem(spacing8)} ${rem(spacing16)};

  ${richtextCommonCss}

  p {
    color: ${({ $isAnswer }) => ($isAnswer ? azure7 : grey9)};
  }

  ${props =>
    props.$highlight &&
    `
      p {
        font-weight: ${fontWeightMedium};
      }
  `}

  ${({ $baseLineHeight, $baseFontSize, $rows }) =>
    $rows &&
    $baseLineHeight &&
    `
    // the 18px is 8px top padding + 8px bottom padding + 2px borders
    min-height: ${rem(
      $rows > 1 ? $baseLineHeight * $rows + 18 : getLineMinHeight($baseFontSize)
    )};
  `}
`;

export const TableCellWrapper = styled.div``;

export const TableCellEditTrigger = styled.button<StandardInputProps>`
  ${standardInputStyles}

  cursor: pointer;
  position: absolute;
  left: ${rem(2)};
  top: ${rem(2)};
  width: calc(100% - ${rem(4)});
  height: calc(100% - ${rem(4)});
  border: none;
  background: transparent;
  border-radius: 0;
`;

type TableCellEditorProps = {
  $highlight?: boolean;
  $baseLineHeight?: number;
  $baseFontSize?: number;
  $rows?: number;
};
export const TableCellEditor = styled(Richtext).attrs({
  isSmall: true,
})<TableCellEditorProps>`
  height: 100%;

  ${({ $baseLineHeight, $baseFontSize, $rows }) =>
    $rows &&
    $baseLineHeight &&
    `
    // the 18px is 8px top padding + 8px bottom padding + 2px borders
    min-height: ${rem(
      $rows > 1 ? $baseLineHeight * $rows + 18 : getLineMinHeight($baseFontSize)
    )};
  `}

  .ProseMirror {
    ${({ $baseLineHeight, $baseFontSize, $rows }) =>
      $rows &&
      $baseLineHeight &&
      `
    // the 18px is 8px top padding + 8px bottom padding + 2px borders
    min-height: ${rem(
      $rows > 1 ? $baseLineHeight * $rows + 18 : getLineMinHeight($baseFontSize)
    )};
  `}
    height: 100%;
    padding: ${rem(spacing8)} ${rem(spacing16)};
    ${props =>
      props.$highlight &&
      `
        background-color: ${coolGrey1};
      `}

    &:hover,
    &:focus {
      border: 1px solid ${purple5};
    }

    &:focus {
      border: 1px solid ${grey10};
    }
  }
`;

export const TableHandleButton = styled.button`
  ${flexCentreAlignCss}
  ${standardFocusState}

  background-color: ${grey1};
  border-radius: ${rem(borderRadius8)};
  border: 1px solid ${grey5};
  width: ${rem(24)};
  height: ${rem(24)};
  padding: 0;
  position: absolute;
  z-index: ${INLINE_SETTINGS_Z_INDEX};

  &:hover {
    cursor: pointer;
  }
`;

type TableColumnSettingsProps = {
  $isVisible: boolean;
};
export const TableColumnSettings = styled.div<TableColumnSettingsProps>`
  ${TableHandleButton} {
    display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
    left: 50%;
    margin-left: -${rem(spacing12)};
    top: -${rem(spacing12)};
  }
`;

export const TableRowSettings = styled.div<TableColumnSettingsProps>`
  ${TableHandleButton} {
    display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
    left: -${rem(spacing12)};
    top: 50%;
    margin-top: -${rem(spacing12)};
    transform: rotate(90deg);
  }
`;

export const TableHeaderControlsCell = styled(TableCell)``;

export const TableHeaderControls = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  top: 50%;
  margin-top: -${rem(12)};
  width: 100%;
  position: absolute;

  ${TooltipWrapperStyled} {
    position: static;
  }
`;
